import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";

import { getIo, updateIo, approveIo, rejectIo, regenerateIo, resolveConflict, getComplianceVerdict, ApiError } from "../api.js";
import { ORDER_FIELDS, LINE_ITEM_FIELDS, TARGETING_FIELDS, getByPath, setByPath, confidenceForPath } from "../lib/ioSchema.js";
import { getUploadedIo, updateUploadedIo, updateUploadedIoStatus, removeUploadedIo } from "../lib/localIos.js";
import StatusBadge, { STATUS_MAP } from "./StatusBadge.jsx";
import FieldEditor from "./FieldEditor.jsx";
import ConflictResolver from "./ConflictResolver.jsx";
import AmendmentDiff from "./AmendmentDiff.jsx";
import SourceDocViewer from "./SourceDocViewer.jsx";
import LawspeakerPanel from "./LawspeakerPanel.jsx";
import ComplianceDrawer, { ComplianceDrawerToggle } from "./ComplianceDrawer.jsx";

// The compliance check goes through Longship's own API (see api.js
// getComplianceVerdict), which signs the Lawspeaker request server-side.
// The browser never talks to Lawspeaker directly — there's no way to
// authenticate that call without shipping a Lawspeaker credential in the
// JS bundle, and Lawspeaker's auth middleware requires one unconditionally.
const LAWSPEAKER_ENABLED =
  (typeof window !== "undefined" && window.__LAWSPEAKER_ENABLED__ !== false) &&
  (import.meta.env?.VITE_LAWSPEAKER_ENABLED !== "false");

// Available models for the regenerate dropdown.
const FALLBACK_MODELS = [
  { value: "", label: "Default (auto)", supports_vision: true, vision_warning: null },
  // Anthropic Claude — all support vision and are good at handwriting
  { value: "claude-sonnet-4-6", label: "Claude Sonnet 4.6 (default)", supports_vision: true, vision_warning: null },
  { value: "claude-opus-4-6", label: "Claude Opus 4.6 (higher quality)", supports_vision: true, vision_warning: null },
  { value: "claude-haiku-4-6", label: "Claude Haiku 4.6 (faster, cheaper)", supports_vision: true, vision_warning: null },
  { value: "claude-sonnet-4-5", label: "Claude Sonnet 4.5 (previous gen)", supports_vision: true, vision_warning: null },
  // Ollama cloud — vision support varies
  { value: "glm-5.2", label: "GLM-5.2 Cloud", supports_vision: false, vision_warning: "Does not support image input — will reject image files" },
  { value: "gemma4:31b", label: "Gemma4 31B Cloud", supports_vision: true, vision_warning: "Poor handwriting accuracy — may misread multiple words" },
  { value: "minimax-m3", label: "MiniMax M3 Cloud", supports_vision: true, vision_warning: "Poor handwriting accuracy — may misread multiple words" },
  { value: "kimi-k2.6", label: "Kimi K2.6 Cloud", supports_vision: true, vision_warning: "High latency (~36s) and unreliable output for image tasks" },
];

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function formatCurrency(n, currency = "USD") {
  if (n == null) return "—";
  const num = typeof n === "number" ? n : Number(n);
  if (Number.isNaN(num)) return String(n);
  try {
    return num.toLocaleString(undefined, { style: "currency", currency, maximumFractionDigits: 2 });
  } catch {
    return `$${num.toFixed(2)}`;
  }
}

// Group a flat field list by its `group` property, preserving order.
function groupFields(fields) {
  const groups = [];
  const byKey = new Map();
  for (const f of fields) {
    if (!byKey.has(f.group)) {
      const g = { group: f.group, fields: [] };
      byKey.set(f.group, g);
      groups.push(g);
    }
    byKey.get(f.group).fields.push(f);
  }
  return groups;
}

const ORDER_GROUPS = groupFields(ORDER_FIELDS);
const LINE_GROUPS = groupFields(LINE_ITEM_FIELDS);
const TARGETING_GROUPS = groupFields(TARGETING_FIELDS);

// Resolve a dotted path on the IO record for the order-level fields. Supports
// "agency_contact.name" style nested paths.
function getOrderValue(io, key) {
  return getByPath(io, key);
}

// --------------------------------------------------------------------------
// SourceDocViewer — imported from ./SourceDocViewer.jsx (handles PDF, image,
// HTML, CSV with proper navigation, zoom, and parsing).
// --------------------------------------------------------------------------

// ---------------------------------------------------------------------------
// LineItemCard — one collapsible line item with FieldEditors.
// ---------------------------------------------------------------------------

function LineItemCard({ index, item, validationFlags, io, onFieldChange }) {
  const [open, setOpen] = useState(false);
  const lineId = item.line_item_id || `line-${index}`;
  const pathPrefix = `line_items.${index}`;

  // Targeting sub-object
  const targeting = item.targeting || {};

  return (
    <div className="line-item-card">
      <div className="line-item-header" onClick={() => setOpen((o) => !o)}>
        <span className="line-item-toggle">{open ? "▾" : "▸"}</span>
        <span className="line-item-id mono">{lineId}</span>
        <span className="line-item-name">{item.line_item_name}</span>
        <span className="line-item-meta">
          {item.ad_format} · {item.pricing_model} · {formatCurrency(item.gross_budget, io.currency)}
        </span>
      </div>
      {open && (
        <div className="line-item-body">
          {LINE_GROUPS.map((g) => (
            <div key={g.group} className="field-group">
              <h5>{g.group}</h5>
              <div className="field-grid">
                {g.fields.map((f) => {
                  const fieldPath = `${pathPrefix}.${f.key}`;
                  const value = getByPath(item, f.key);
                  const issues = (validationFlags?.[fieldPath] || []).map((i) => ({
                    code: i.code,
                    message: i.message,
                    severity: i.severity,
                    suggestion: i.suggestion,
                  }));
                  return (
                    <FieldEditor
                      key={f.key}
                      fieldPath={fieldPath}
                      label={f.label}
                      value={value}
                      type={f.type}
                      options={f.options}
                      confidence={confidenceForPath(io, fieldPath)}
                      validationIssues={issues}
                      onChange={(v) => onFieldChange(fieldPath, v)}
                    />
                  );
                })}
              </div>
            </div>
          ))}

          {/* Targeting sub-object */}
          <div className="field-group">
            <h5>Targeting</h5>
            <div className="field-grid">
              {TARGETING_GROUPS.flatMap((g) => g.fields).map((f) => {
                const fieldPath = `${pathPrefix}.targeting.${f.key}`;
                const value = getByPath(targeting, f.key);
                const issues = (validationFlags?.[fieldPath] || []).map((i) => ({
                  code: i.code,
                  message: i.message,
                  severity: i.severity,
                  suggestion: i.suggestion,
                }));
                return (
                  <FieldEditor
                    key={f.key}
                    fieldPath={fieldPath}
                    label={f.label}
                    value={value}
                    type={f.type}
                    options={f.options}
                    confidence={confidenceForPath(io, fieldPath)}
                    validationIssues={issues}
                    onChange={(v) => onFieldChange(fieldPath, v)}
                  />
                );
              })}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// ReviewPane — the main review screen.
// ---------------------------------------------------------------------------

export default function ReviewPane() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const [io, setIo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [actionMsg, setActionMsg] = useState(null);
  const [rejectOpen, setRejectOpen] = useState(false);
  const [rejectReason, setRejectReason] = useState("");

  // Compliance warnings drawer. Opened by the 20px toggle, or automatically
  // when arriving from the Policies page (?drawer=warnings on "Open IO
  // review"), so the flagged detail lands on the IO it belongs to.
  const [warningsOpen, setWarningsOpen] = useState(
    () => new URLSearchParams(window.location.search).get("drawer") === "warnings"
  );

  // Lawspeaker compliance verdict state
  const [lsVerdict, setLsVerdict] = useState(null);
  const [lsLoading, setLsLoading] = useState(false);
  const [lsError, setLsError] = useState(null);
  const [lsLastChecked, setLsLastChecked] = useState(null);
  const [lsOverrides, setLsOverrides] = useState(() => new Set());

  // Model selection for regeneration.
  const [models, setModels] = useState(FALLBACK_MODELS);
  const [regenModel, setRegenModel] = useState(() => {
    return localStorage.getItem("longship.selectedModel") || "";
  });

  // Fetch available models from the parser service on mount.
  useEffect(() => {
    const parserUrl = import.meta.env.VITE_PARSER_URL || "";
    const url = parserUrl ? `${parserUrl}/models` : "/parser/models";
    fetch(url)
      .then((res) => (res.ok ? res.json() : null))
      .then((data) => {
        if (data?.models?.length) {
          setModels([
            { value: "", label: "Default (auto)", supports_vision: true, vision_warning: null },
            ...data.models.map((m) => ({
              value: m.value,
              label: m.label,
              supports_vision: m.supports_vision ?? true,
              vision_warning: m.vision_warning ?? null,
            })),
          ]);
        }
      })
      .catch(() => {});
  }, []);

  // -------------------------------------------------------------------------
  // Load IO
  // -------------------------------------------------------------------------
  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      // Local (upload-only) IOs never exist in the backend — id.startsWith
      // matches the id shape saveUploadedIos() generates — so skip the API
      // call entirely and read straight from localStorage.
      if (id?.startsWith("local-")) {
        const local = getUploadedIo(id);
        if (!local) {
          throw new ApiError({ code: "NOT_FOUND", message: "Local upload not found — it may have been removed.", status: 404 });
        }
        setIo(local);
        return;
      }
      const data = await getIo(id);
      setIo(data);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : String(err));
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    load();
  }, [load]);

  // -------------------------------------------------------------------------
  // Lawspeaker compliance check
  // -------------------------------------------------------------------------
  const checkLawspeaker = useCallback(async () => {
    if (!LAWSPEAKER_ENABLED) {
      setLsError(null);
      setLsVerdict(null);
      return;
    }
    if (!io) return;

    setLsLoading(true);
    setLsError(null);

    // Build Lawspeaker request from the IO record, mirroring what the
    // server-side validator does so we display the same verdict shape.
    const lineItems = io.line_items || io.lineItems || [];
    const platforms = new Set();
    const categories = new Set();
    const countries = new Set();
    const usStates = new Set();

    const PLATFORM_MAP = {
      google: "google", googleads: "google", youtube: "google",
      meta: "meta", facebook: "meta", instagram: "meta",
      tiktok: "tiktok",
      amazon: "amazon", amazonads: "amazon",
      openai: "openai", chatgpt: "openai",
      dv360: "dv360", gam: "gam", simplifi: "simplifi",
      adgentek: "google", mock: "google",
    };

    const text = [
      io.advertiser_name || io.advertiserName || "",
      io.special_instructions || io.specialInstructions || "",
    ].join(" ").toLowerCase();

    const CATEGORY_KEYWORDS = [
      { category: "Alcohol", keywords: ["alcohol", "wine", "beer", "spirits", "liquor"] },
      { category: "Gambling", keywords: ["gambling", "casino", "betting", "sportsbook"] },
      { category: "Cannabis", keywords: ["cannabis", "thc", "marijuana", "cbd"] },
      { category: "Healthcare", keywords: ["health", "medical", "pharma"] },
      { category: "Financial services", keywords: ["finance", "invest", "wealth", "bank"] },
      { category: "Tobacco", keywords: ["tobacco", "vaping", "vape"] },
      { category: "Dating", keywords: ["dating", "romance"] },
      { category: "Political", keywords: ["political", "election"] },
    ];

    for (const { category, keywords } of CATEGORY_KEYWORDS) {
      if (keywords.some((kw) => text.includes(kw))) categories.add(category);
    }

    for (const item of lineItems) {
      const plat = item.platform || item.targetPlatform || item.target_platform;
      if (plat) {
        const m = PLATFORM_MAP[plat.toLowerCase()];
        if (m) platforms.add(m);
      }
      const geos = item.targetCountries || item.target_countries || item.targeting?.geo;
      if (Array.isArray(geos)) for (const g of geos) countries.add(g);
      const ss = item.targetUSStates || item.target_us_states || item.geotargeting?.usStates;
      if (Array.isArray(ss)) for (const s of ss) usStates.add(s);
    }

    if (platforms.size === 0) platforms.add("google");
    if (countries.size === 0) countries.add("US");
    if (categories.size === 0) categories.add("General");

    const request = {
      campaign: {
        title: io.campaign_name || io.campaignName || io.advertiser_name || io.advertiserName || "Unknown Campaign",
        description: io.description || "",
        categories: [...categories],
      },
      targeting: {
        platforms: [...platforms],
        countries: [...countries],
        us_states: [...usStates],
        age_targeting: "all",
      },
      context: { io_number: io.io_number || io.ioNumber },
    };

    try {
      const data = await getComplianceVerdict(request);
      if (data?.enabled === false) {
        // Gate is disabled server-side (LAWSPEAKER_ENABLED=false on the API).
        setLsVerdict(null);
        setLsError(null);
        return;
      }
      setLsVerdict(data);
      setLsLastChecked(new Date().toLocaleString());
    } catch (err) {
      setLsError(err.message || "Lawspeaker check failed");
      setLsVerdict(null);
    } finally {
      setLsLoading(false);
    }
  }, [io]);

  useEffect(() => {
    if (io) checkLawspeaker();
  }, [io, checkLawspeaker]);

  const handleLsOverride = useCallback((platformKey, code, reason) => {
    const key = `${platformKey}-${code}-${reason.length}`;
    setLsOverrides((prev) => {
      const next = new Set(prev);
      next.add(key);
      return next;
    });
    // In a full integration, this would POST to an audit endpoint
    console.info("[Lawspeaker] Override applied", { platformKey, code, reason, ioNumber: io?.io_number });
  }, [io]);

  // -------------------------------------------------------------------------
  // Field edits
  // -------------------------------------------------------------------------
  const handleFieldChange = useCallback((path, value) => {
    setIo((prev) => {
      if (!prev) return prev;
      // Array-typed fields are edited as comma-separated text; split here.
      const parts = path.split(".");
      const lastKey = parts[parts.length - 1];
      const fieldDef =
        ORDER_FIELDS.find((f) => f.key === path) ||
        LINE_ITEM_FIELDS.find((f) => f.key === lastKey) ||
        TARGETING_FIELDS.find((f) => f.key === lastKey);
      let finalValue = value;
      if (fieldDef && fieldDef.array && typeof value === "string") {
        finalValue = value
          .split(",")
          .map((s) => s.trim())
          .filter(Boolean);
      }
      return setByPath(prev, path, finalValue);
    });
  }, []);

  // -------------------------------------------------------------------------
  // Actions: Save, Approve, Reject, Regenerate
  // -------------------------------------------------------------------------
  function flash(msg, kind = "ok") {
    setActionMsg({ msg, kind });
    setTimeout(() => setActionMsg(null), 4000);
  }

  // Uploaded IOs are now saved to the backend DB on upload, but they also
  // exist in localStorage with a local-only id. We use io_number (the backend
  // id) for API calls, and fall back to localStorage if the backend 404s.
  const isLocalOnly = io?.source === "upload";

  async function handleSave() {
    if (!io) return;
    setSaving(true);
    try {
      if (isLocalOnly) {
        // Try backend first (IO should exist in backend from upload)
        try {
          const patch = { ...io };
          delete patch.id;
          delete patch.source;
          delete patch.sourceFile;
          delete patch.sourceDocDataUrl;
          delete patch.extractedSections;
          delete patch.status;
          delete patch.validationFlags;
          delete patch.createdAt;
          delete patch.updatedAt;
          await updateIo(io.io_number || id, patch);
          flash("Saved.");
        } catch (err) {
          if (err.status === 404) {
            updateUploadedIo(id, io);
            flash("Saved (stored locally only — not in backend).");
          } else {
            throw err;
          }
        }
      } else {
        // Build a clean patch — strip non-schema keys the API will reject.
        const patch = { ...io };
        delete patch.id;
        delete patch.status;
        delete patch.validationFlags;
        delete patch.createdAt;
        delete patch.updatedAt;
        delete patch.approvedBy;
        delete patch.approvedAt;
        delete patch.rejectionReason;
        delete patch.rejectedAt;
        const updated = await updateIo(id, patch);
        setIo(updated);
        flash("Saved.");
      }
    } catch (err) {
      flash(`Save failed: ${err instanceof ApiError ? err.message : String(err)}`, "error");
    } finally {
      setSaving(false);
    }
  }

  async function handleApprove() {
    if (!io) return;
    setSaving(true);
    try {
      // Always try the backend first — uploaded IOs are saved to the backend DB
      const backendId = io.io_number || id;
      try {
        await approveIo(backendId, { approver: "longship@adgentek.ai" });
        if (isLocalOnly) {
          updateUploadedIoStatus(id, "approved");
          setIo((prev) => ({ ...prev, status: "approved" }));
        }
        flash("Approved — redirecting to preview…");
        // After approve → campaign preview
        setTimeout(() => navigate(`/ios/${encodeURIComponent(backendId)}/preview`), 600);
      } catch (err) {
        if (err.status === 404 && isLocalOnly) {
          updateUploadedIoStatus(id, "approved");
          setIo((prev) => ({ ...prev, status: "approved" }));
          flash("Approved (stored locally only — not in backend).");
        } else {
          throw err;
        }
      }
    } catch (err) {
      flash(`Approve failed: ${err instanceof ApiError ? err.message : String(err)}`, "error");
    } finally {
      setSaving(false);
    }
  }

  async function handleReject() {
    if (!io || !rejectReason.trim()) return;
    setSaving(true);
    try {
      const backendId = io.io_number || id;
      try {
        await rejectIo(backendId, { reason: rejectReason.trim() });
        if (isLocalOnly) {
          updateUploadedIo(id, { status: "rejected", rejectionReason: rejectReason.trim() });
          setIo((prev) => ({ ...prev, status: "rejected", rejectionReason: rejectReason.trim() }));
        } else {
          await load();
        }
      } catch (err) {
        if (err.status === 404 && isLocalOnly) {
          updateUploadedIo(id, { status: "rejected", rejectionReason: rejectReason.trim() });
          setIo((prev) => ({ ...prev, status: "rejected", rejectionReason: rejectReason.trim() }));
        } else {
          throw err;
        }
      }
      setRejectOpen(false);
      setRejectReason("");
      flash("IO rejected.");
    } catch (err) {
      flash(`Reject failed: ${err instanceof ApiError ? err.message : String(err)}`, "error");
    } finally {
      setSaving(false);
    }
  }

  function handleRemoveLocal() {
    if (!io) return;
    if (!window.confirm(`Remove ${io.io_number || io.id}? This can't be undone.`)) return;
    removeUploadedIo(id);
    navigate("/dashboard");
  }

  async function handleRegenerate() {
    if (!io) return;
    setSaving(true);
    try {
      const body = {};
      if (regenModel) body.model = regenModel;
      const job = await regenerateIo(id, body);
      flash(`Regeneration queued (job ${job?.jobId || "?"}). Poll for updates.`);
    } catch (err) {
      flash(`Regenerate failed: ${err instanceof ApiError ? err.message : String(err)}`, "error");
    } finally {
      setSaving(false);
    }
  }

  // -------------------------------------------------------------------------
  // Conflict resolution (PRD §9.3)
  //
  // Wires the ConflictResolver's per-issue action buttons. For local-only
  // IOs we update the localStorage record directly. For backend IOs we
  // call the API and refresh the IO state to pick up the new resolutions.
  // -------------------------------------------------------------------------
  async function handleResolveConflict(issue, action, payload = {}) {
    if (!io) return;
    const body = {
      code: issue.code,
      path: issue.path,
      action,
      ...payload,
    };

    if (isLocalOnly) {
      // Local-only path: append to a resolutions list on the record.
      const resolution = {
        id: `local-${Date.now()}`,
        ...body,
        actor: "longship@adgentek.ai",
        resolvedAt: new Date().toISOString(),
      };
      const updated = {
        ...io,
        validationFlags: {
          ...(io.validationFlags || {}),
          resolutions: [
            ...((io.validationFlags && io.validationFlags.resolutions) || []),
            resolution,
          ],
        },
      };
      // Apply override to local state.
      if (action === "override" && issue.path && payload.value !== undefined) {
        const merged = setByPath(updated, issue.path, payload.value);
        updateUploadedIo(id, merged);
        setIo(merged);
      } else {
        updateUploadedIo(id, updated);
        setIo(updated);
      }
      flash(`Local: ${action} recorded for ${issue.code}.`);
      return { id: resolution.id };
    }

    const result = await resolveConflict(id, body);
    flash(`Conflict ${action} recorded for ${issue.code}.`);
    // Refresh IO so the new resolution shows up in the panel.
    if (result?.io) {
      setIo(result.io);
    } else {
      await load();
    }
    return result?.resolution;
  }

  // -------------------------------------------------------------------------
  // Build validation result for the ConflictResolver from the IO's flags.
  // The API attaches validationFlags.budgetReconciliation; in a full build the
  // validation package output ({ ok, errors, warnings, info, fieldFlags })
  // would be attached to the IO. We synthesize the three buckets from any
  // structured flags present, and from fieldFlags if present.
  // -------------------------------------------------------------------------
  const validationBuckets = useMemo(() => {
    const errors = [];
    const warnings = [];
    const info = [];
    if (!io) return { errors, warnings, info, fieldFlags: {} };

    const flags = io.validationFlags || {};
    const fieldFlags = io.validationResult?.fieldFlags || {};

    // Budget reconciliation (warning-only per schema).
    if (flags.budgetReconciliation && flags.budgetReconciliation.ok === false) {
      warnings.push({
        code: "BUDGET_RECONCILIATION",
        severity: "warning",
        path: "total_gross_budget",
        message: flags.budgetReconciliation.message || "Budget reconciliation discrepancy.",
        suggestion: "Review line item gross_budget values vs total_gross_budget.",
      });
    }

    // If the API attached a full validation result, fold it in.
    if (io.validationResult) {
      const vr = io.validationResult;
      if (Array.isArray(vr.errors)) errors.push(...vr.errors);
      if (Array.isArray(vr.warnings)) warnings.push(...vr.warnings);
      if (Array.isArray(vr.info)) info.push(...vr.info);
    }

    // Promote any field-level error flags not already captured.
    for (const [path, list] of Object.entries(fieldFlags)) {
      if (!Array.isArray(list)) continue;
      for (const issue of list) {
        if (issue.severity === "error" && !errors.some((e) => e.path === path && e.code === issue.code)) {
          errors.push({ ...issue, path });
        } else if (issue.severity === "warning" && !warnings.some((w) => w.path === path && w.code === issue.code)) {
          warnings.push({ ...issue, path });
        } else if (issue.severity === "info") {
          info.push({ ...issue, path });
        }
      }
    }

    return { errors, warnings, info, fieldFlags };
  }, [io]);

  // -------------------------------------------------------------------------
  // Render
  // -------------------------------------------------------------------------
  if (loading) {
    return <div className="review-pane container"><p>Loading IO…</p></div>;
  }
  if (error) {
    return (
      <div className="review-pane container">
        <div className="alert alert-error">Failed to load IO: {error}</div>
        <button className="btn" onClick={load}>Retry</button>
      </div>
    );
  }
  if (!io) {
    return <div className="review-pane container"><p>IO not found.</p></div>;
  }

  return (
    <div className="review-pane">
      {/* TOP: IO header */}
      <div className="review-header">
        <div className="review-header-left">
          <h2 className="mono">{io.io_number}</h2>
          <StatusBadge status={io.status} />
          {isLocalOnly && (
            <span className="status-badge badge-gray local-badge" title="Extracted via Upload — stored in this browser only">
              LOCAL
            </span>
          )}
          <span className="review-header-budget">
            {formatCurrency(io.total_gross_budget, io.currency)} {io.currency}
          </span>
          <span className="muted">{io.advertiser_name} · {io.agency}</span>
        </div>
        <div className="review-header-actions">
          {!isLocalOnly && (
            <>
              <label className="model-selector-inline">
                <select
                  value={regenModel}
                  onChange={(e) => setRegenModel(e.target.value)}
                  title="AI model for regeneration"
                  disabled={saving}
                >
                  {models.map((m) => (
                    <option key={m.value} value={m.value}>{m.label}</option>
                  ))}
                </select>
                {(() => {
                  // Only show vision warning when the source file is an image
                  const sourceFile = io?._meta?.source_file || "";
                  const ext = sourceFile.split(".").pop()?.toLowerCase();
                  const imageExts = ["png", "jpg", "jpeg", "webp", "tiff", "tif", "bmp"];
                  if (!imageExts.includes(ext)) return null;
                  const m = models.find((m) => m.value === regenModel);
                  if (!m?.vision_warning) return null;
                  const isBlocker = m.supports_vision === false;
                  return (
                    <span className={`model-vision-warning ${isBlocker ? "model-vision-warning-blocker" : ""}`} title={m.vision_warning}>
                      ⚠ {isBlocker ? "⛔ " : ""}{m.vision_warning}
                    </span>
                  );
                })()}
              </label>
              <button className="btn" onClick={handleRegenerate} disabled={saving}>Regenerate</button>
            </>
          )}
          <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
            {saving ? "Saving…" : "Save"}
          </button>
          <div className="btn btn-success dropdown-wrapper">
            <select
              className="btn-dropdown-native"
              value={io.status || "draft"}
              onChange={(e) => {
                if (e.target.value === "rejected") {
                  setRejectOpen(true);
                } else if (e.target.value === "approved") {
                  handleApprove();
                }
              }}
              disabled={saving}
              title="Approve or Reject"
            >
              <option value={io.status || "draft"}>
                {saving ? "Working…" : (STATUS_MAP[io.status]?.label || "Pending Review")}
              </option>
              {(io.status !== "approved") && <option value="approved">Approve</option>}
              {(io.status !== "rejected") && <option value="rejected">Reject</option>}
            </select>
          </div>
          {isLocalOnly && (
            <button className="btn btn-danger-outline" onClick={handleRemoveLocal} disabled={saving}>
              Remove
            </button>
          )}
        </div>
      </div>

      {actionMsg && (
        <div className={`alert alert-${actionMsg.kind === "error" ? "error" : "success"}`}>
          {actionMsg.msg}
        </div>
      )}

      {rejectOpen && (
        <div className="reject-panel">
          <label>
            <span>Rejection reason (required):</span>
            <textarea
              rows={3}
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              placeholder="Explain why this IO is being rejected…"
            />
          </label>
          <div className="reject-panel-actions">
            <button className="btn btn-danger" onClick={handleReject} disabled={saving || !rejectReason.trim()}>
              Confirm Reject
            </button>
            <button className="btn" onClick={() => { setRejectOpen(false); setRejectReason(""); }} disabled={saving}>
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* SPLIT PANE */}
      <div className="review-split">
        {/* LEFT: source document */}
        <div className="review-pane-left">
          <h4 className="pane-title">Source Document</h4>
          <SourceDocViewer io={io} />
        </div>

        {/* RIGHT: extracted fields */}
        <div className="review-pane-right">
          <h4 className="pane-title">Extracted Fields</h4>

          {/* Order-level */}
          {ORDER_GROUPS.map((g) => (
            <div key={g.group} className="field-group">
              <h5>{g.group}</h5>
              <div className="field-grid">
                {g.fields.map((f) => {
                  const value = getOrderValue(io, f.key);
                  const issues = (validationBuckets.fieldFlags[f.key] || []).map((i) => ({
                    code: i.code,
                    message: i.message,
                    severity: i.severity,
                    suggestion: i.suggestion,
                  }));
                  return (
                    <FieldEditor
                      key={f.key}
                      fieldPath={f.key}
                      label={f.label}
                      value={value}
                      type={f.type}
                      options={f.options}
                      confidence={confidenceForPath(io, f.key)}
                      validationIssues={issues}
                      onChange={(v) => handleFieldChange(f.key, v)}
                    />
                  );
                })}
              </div>
            </div>
          ))}

          {/* Line items */}
          <div className="field-group">
            <h5>Line Items ({(io.line_items || []).length})</h5>
            {(io.line_items || []).map((item, idx) => (
              <LineItemCard
                key={item.line_item_id || idx}
                index={idx}
                item={item}
                io={io}
                validationFlags={validationBuckets.fieldFlags}
                onFieldChange={handleFieldChange}
              />
            ))}
          </div>

          {/* Amendment diff (only if amendment detected) */}
          {io.amendmentResult && (
            <div className="field-group">
              <h5>Amendment Diff</h5>
              <AmendmentDiff amendmentResult={io.amendmentResult} />
            </div>
          )}
        </div>
      </div>

      {/* BOTTOM: validation panel, full width.
          The Lawspeaker compliance detail now lives in the Policy Checker
          drawer on the right, so this row is a single full-width column. */}
      <div className="review-bottom-grid">
        <div className="review-validation">
          <h4 className="pane-title">Validation</h4>
          <ConflictResolver
            errors={validationBuckets.errors}
            warnings={validationBuckets.warnings}
            info={validationBuckets.info}
            resolutions={io.validationFlags?.resolutions || []}
            onResolve={handleResolveConflict}
            onFieldOverride={handleFieldChange}
          />
        </div>
      </div>

      {/* Warnings + "What to fix" for this IO, in a full-height right drawer.
          The "Policy Checker" tab on the right edge opens/closes it. */}
      <ComplianceDrawerToggle
        open={warningsOpen}
        onClick={() => setWarningsOpen((o) => !o)}
        disabled={!lsVerdict && !lsError}
        title={
          lsVerdict
            ? `${warningsOpen ? "Close" : "Show"} warnings and what to fix`
            : "Run a compliance check to see warnings"
        }
      />

      <ComplianceDrawer
        open={warningsOpen}
        onClose={() => setWarningsOpen(false)}
        ioNumber={io.io_number || io.id}
        title={io.campaign_name || io.advertiser_name}
        verdict={lsVerdict?.verdict}
        error={lsError}
        panel={
          <LawspeakerPanel
            verdict={lsVerdict}
            loading={lsLoading}
            error={lsError}
            onOverride={handleLsOverride}
            overrides={lsOverrides}
            lastChecked={lsLastChecked}
          />
        }
        actions={
          LAWSPEAKER_ENABLED ? (
            <button className="btn btn-sm btn-ghost compliance-resync" onClick={checkLawspeaker} disabled={lsLoading}>
              {lsLoading ? "Checking…" : "Re-check compliance"}
            </button>
          ) : null
        }
      />
    </div>
  );
}